import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  Fragment,
  useCallback,
} from "react";

import { SoundContext } from "../App";
import ratchet from "./ratchet.mp3";
import timerEnd from "./timerEnd.mp3";
import ArrowIcon from "./LeftArrowIcon.svg";
import PointerIcon from "./pointer.svg";
import "./PomodoroScale.scss";

const maxTime = 25;
const scaleGap = 40;

const PomodoroScale = () => {
  const scrollAudio = new Audio(ratchet);
  const timerEndAudio = new Audio(timerEnd);

  const stopAllOtherAudio = useCallback(() => {
    scrollAudio.pause();
    timerEndAudio.pause();
  }, [scrollAudio, timerEndAudio]);

  const soundContext = useContext(SoundContext);
  const isSoundEnabled = soundContext.get;

  // Below logic is to handle scale width and dragging
  const [scaleWidth, setScaleWidth] = useState(null);
  const [inintialScalePosition, setInitialScalePosition] = useState(null);
  const [scalePosition, setScalePosition] = useState({
    x: 0,
  });
  const [mousePosition, setMousePosition] = useState({
    x: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [showdragArrowIcon, setShowDragArrowIcon] = useState(true);

  useEffect(() => {
    const scalesWidth = document.getElementById("pomodoro-scale-wrapper")
      .offsetWidth;
    setScaleWidth(scalesWidth);
    const halfWidth = scalesWidth / 2;
    const roundToNearestMultipleHalfWidth =
      Math.round(halfWidth / scaleGap) * scaleGap;
    setInitialScalePosition(roundToNearestMultipleHalfWidth);
    setScalePosition({ x: roundToNearestMultipleHalfWidth });
  }, []);

  const prevMousePosition = useRef(null);
  const prevScalePosition = useRef(null);

  useEffect(() => {
    prevScalePosition.current = scalePosition;
  }, [scalePosition]);

  useEffect(() => {
    if (prevMousePosition.current) {
      const dx = prevMousePosition.current.x - mousePosition.x;
      if (prevScalePosition.current) {
        let newScalePositionX = prevScalePosition.current.x - dx;
        if (newScalePositionX > inintialScalePosition) {
          newScalePositionX = inintialScalePosition;
        }
        const maxScalePosition =
          inintialScalePosition + scaleGap - (maxTime + 1) * scaleGap;
        if (newScalePositionX < maxScalePosition) {
          newScalePositionX = maxScalePosition;
        }
        setScalePosition({ x: newScalePositionX });
      }
    }
    prevMousePosition.current = mousePosition;
  }, [mousePosition, inintialScalePosition]);

  const onScaleMouseUp = () => {
    if (isDragging) {
      if (isSoundEnabled) {
        stopAllOtherAudio();
        scrollAudio.play();
      }
      setIsDragging(false);
      const roundToNearestMultipleNewScalePositionX =
        Math.round(prevScalePosition.current.x / scaleGap) * scaleGap;
      setScalePosition({ x: roundToNearestMultipleNewScalePositionX });
      const diffInScaleLength =
        inintialScalePosition - roundToNearestMultipleNewScalePositionX;
      startNewTimer(diffInScaleLength / scaleGap);
    }
  };

  const onScaleMouseMove = (e) => {
    if (!isDragging) {
      return;
    }
    let positionX;
    if (e.type === "touchmove") {
      var touch = e.touches[0] || e.changedTouches[0];
      positionX = touch.pageX;
    } else {
      positionX = e.clientX;
    }
    setShowDragArrowIcon(false);
    setMousePosition({ x: positionX });
  };

  const onScaleMouseDown = (e) => {
    let positionX;
    if (e.type === "touchstart") {
      var touch = e.touches[0] || e.changedTouches[0];
      positionX = touch.pageX;
    } else {
      positionX = e.clientX;
    }
    setIsDragging(true);
    if (isSoundEnabled) {
      stopAllOtherAudio();
      scrollAudio.play();
    }
    prevMousePosition.current.x = positionX;
    setMousePosition({ x: positionX });
  };

  // Below logic is for setting time
  const [timeInSec, setTimeInSec] = useState(0);
  const timeInMin = Math.floor(timeInSec / 60);
  const remainingSec = timeInSec - timeInMin * 60;
  const timeInMinForDragger = Math.ceil(timeInSec / 60);
  const timeRef = useRef();
  const timerRef = useRef();

  useEffect(() => {
    if (inintialScalePosition) {
      const draggerPositionChange = timeInMinForDragger * scaleGap;
      setScalePosition({ x: inintialScalePosition - draggerPositionChange });
    }
  }, [timeInMinForDragger, inintialScalePosition]);

  const endTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (isSoundEnabled) {
      stopAllOtherAudio();
      timerEndAudio.play();
    }
  }, [isSoundEnabled, stopAllOtherAudio, timerEndAudio]);

  const startNewTimer = (time) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimeInSec(time * 60);
    timerRef.current = setInterval(() => {
      setTimeInSec(timeRef.current > 0 ? timeRef.current - 1 : timeRef.current);
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current && timeRef.current !== 0 && timeInSec === 0) {
      endTimer();
    }
    timeRef.current = timeInSec;
  }, [timeInSec, endTimer]);

  return (
    <div id="pomodoro-scale-wrapper" className="pomodoro-scale-wrapper">
      {!!scaleWidth && (
        <>
          <div className="digital-time">
            <span>{`${timeInMin < 10 ? `0${timeInMin}` : timeInMin}:${
              remainingSec < 10 ? `0${remainingSec}` : remainingSec
            }`}</span>
          </div>
          <div className="pomodoro-scale">
            {showdragArrowIcon && (
              <div
                className="drag-arrow-icon"
                style={{
                  position: "absolute",
                  left: `${inintialScalePosition - 180}px`,
                  top: "15px",
                }}
              >
                <span>Drag the scale to set Timer</span>
                <img src={ArrowIcon} alt="->" />
              </div>
            )}
            <div
              id="pomodoro-small-scales"
              style={{
                position: "absolute",
                left: `${scalePosition.x}px`,
                cursor: "grab",
                paddingTop: "40px",
                height: "30px",
                display: "flex",
              }}
            >
              {[...Array(maxTime + 1)].map((value, index) => {
                return (
                  <Fragment key={index}>
                    <span
                      className="scale"
                      style={{
                        alignSelf: "stretch",
                        boxSizing: "border-box",
                        borderLeft: "1.0px solid white",
                        marginLeft: `${
                          index === 0 ? "0px" : `${scaleGap - 1}px`
                        }`,
                      }}
                    ></span>
                    <span
                      style={{
                        position: "absolute",
                        left: `${
                          index === 0 ? "0px" : `${index * scaleGap - 8}px`
                        }`,
                        top: "0px",
                      }}
                    >
                      {index < 10 ? `0${index}` : index}
                    </span>
                  </Fragment>
                );
              })}
            </div>
            <div
              id="pomodoro-big-scales"
              onMouseDown={onScaleMouseDown}
              onMouseMove={onScaleMouseMove}
              onMouseUp={onScaleMouseUp}
              onMouseLeave={onScaleMouseUp}
              onTouchStart={onScaleMouseDown}
              onTouchMove={onScaleMouseMove}
              onTouchEnd={onScaleMouseUp}
              style={{
                position: "absolute",
                left: `${scalePosition.x}px`,
                cursor: `${isDragging ? "grabbing" : "grab"}`,
                height: "50px",
                marginTop: "20px",
                display: "flex",
              }}
            >
              {[...Array(maxTime / 5 + 1)].map((value, index) => {
                return (
                  <span
                    key={index}
                    className="scale"
                    style={{
                      alignSelf: "stretch",
                      boxSizing: "border-box",
                      borderLeft: "2px solid white",
                      marginLeft: `${
                        index === 0 ? "0px" : `${scaleGap * 5 - 2}px`
                      }`,
                    }}
                  ></span>
                );
              })}
            </div>
            <div className="pomodoro-pointer">
              <img
                src={PointerIcon}
                alt="^"
                draggable="false"
                style={{
                  position: "absolute",
                  top: "80px",
                  left: `${inintialScalePosition - 15}px`,
                  height: "30px",
                  width: "30px",
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PomodoroScale;
